import { t } from 'i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { MimeType } from 'core/enums';
import { getDomainUrl } from 'core/utils';
import {
  VendorQuoteImportAnomaly,
  VendorQuoteImportManufacturerStatus,
  VendorQuoteImportOutcome,
} from 'dashboard/enums';
import { IVendorQuoteImportAttempt, IVendorQuoteImportFailureReason } from 'dashboard/types';
import { Color } from 'theme/enums';

const SECONDS_PER_MINUTE = 60;

export interface IAnomalyBadge {
  color: Color;
  key: string;
  label: string;
  tooltip: string | null;
}

// Takes the attempt rather than a list of loose counts: every flag that needs a
// number reads it off a different field, and three positional numbers are one
// transposed argument away from a wrong badge.
export const getAnomalyBadges = (attempt?: IVendorQuoteImportAttempt): IAnomalyBadge[] =>
  (attempt?.anomalies ?? [])
    .filter((anomaly): anomaly is string => typeof anomaly === 'string')
    // The flag is a bare, content-derived string (e.g. "double_persist",
    // "stale_rows_cleared") — a stable React key for the badge. The cleared-row
    // count rides in the separate `staleStagedRowsCleared` field, not the flag.
    .map((anomaly) => {
      if (anomaly === VendorQuoteImportAnomaly.DOUBLE_PERSIST) {
        return {
          key: anomaly,
          label: t('labelDoublePersist'),
          tooltip: t('tooltipDoublePersist'),
          color: Color.WARNING,
        };
      }

      // Nothing landed in the quote — louder than the other flags, which
      // describe an oddity in a run that still delivered parts.
      if (anomaly === VendorQuoteImportAnomaly.NO_PARTS_IMPORTED) {
        return {
          key: anomaly,
          label: t('labelNoPartsImported'),
          tooltip: t('tooltipNoPartsImported'),
          color: Color.DANGER,
        };
      }

      // Lines the importer dropped before staging (no part number). The upload
      // succeeded with fewer lines than the document held, so it is a warning,
      // not a failure — and no staged-row figure counts these.
      if (anomaly === VendorQuoteImportAnomaly.ROWS_SKIPPED) {
        // An absent count falls through to the raw-badge fallback: naming the
        // flag beats claiming a confident "0 lines".
        if (Number.isFinite(attempt?.skippedPartCount)) {
          return {
            key: anomaly,
            label: t('labelRowsSkipped', { count: attempt?.skippedPartCount }),
            tooltip: t('tooltipRowsSkipped', { count: attempt?.skippedPartCount }),
            color: Color.WARNING,
          };
        }
      }

      if (anomaly === VendorQuoteImportAnomaly.STALE_ROWS_CLEARED) {
        // An absent/unparseable count falls through to the raw-badge fallback below —
        // surfacing the raw flag beats reporting a confident "0 rows".
        if (Number.isFinite(attempt?.staleStagedRowsCleared)) {
          return {
            key: anomaly,
            label: t('labelGuardsBypassed', { count: attempt?.staleStagedRowsCleared }),
            tooltip: t('tooltipStaleRowsCleared', { count: attempt?.staleStagedRowsCleared }),
            color: Color.WARNING,
          };
        }
      }

      // Unknown server flag (newer BE than FE): still surface it as a warning badge.
      return { key: anomaly, label: anomaly, tooltip: null, color: Color.WARNING };
    });

export const getAnomalyLabels = (attempt?: IVendorQuoteImportAttempt): string =>
  getAnomalyBadges(attempt)
    .map((badge) => badge.label)
    .join(', ');

// Only PDFs get a preview: the viewer renders nothing else, and roughly a third
// of these uploads are spreadsheets. Read off the recorded mime rather than the
// file name — the name is whatever the vendor called the file.
export const isPreviewablePdf = (attempt?: IVendorQuoteImportAttempt): boolean =>
  attempt?.fileMime === MimeType.PDF && !!attempt?.fileId;

// This dashboard's own download route, not the generic /files/index/download:
// that one resolves access through the record ACL, which requires the file's
// quote to belong to the acting user's current company. The report is
// deliberately cross-company, so every row from elsewhere came back as a
// redirect to the sign-in page instead of a PDF. The route here carries the
// same rank-7 gate as the report itself.
const IMPORT_FILE_PATH = '/quote/vq-import-log/download-file';

// Absolute, because a click on the link is a top-level navigation: it sends
// `Accept: text/html`, which the dev server refuses to proxy and answers with
// the SPA shell instead. Not a react-router path either, so no /v2 prefix.
export const getImportFileDownloadUrl = (fileId?: number | null): string | null =>
  fileId ? `${getDomainUrl()}${IMPORT_FILE_PATH}?file_id=${fileId}` : null;

// Relative, because the viewer fetches this itself rather than navigating to it,
// so it needs no origin: the app and the API share one. That also keeps the
// preview independent of REACT_APP_DOMAIN_URL, which points wherever the local
// .env says and is not necessarily the instance the proxy talks to.
//
// It still cannot be exercised from localhost: the dev hosts sit behind a
// basic-auth gate that admits API calls but redirects anything that looks like
// a browser fetch to /basicAuth/index.html, and the viewer's request looks like
// the latter. Verify the preview on the deployed instance.
export const getImportFilePreviewUrl = (fileId?: number | null): string | null =>
  fileId ? `${IMPORT_FILE_PATH}?file_id=${fileId}` : null;

// Both halves in one cell: the name is what an admin recognises, the id is what
// they paste into a ticket or a query. Either half can be absent — a user with
// no corporate membership has neither — so the label degrades rather than
// rendering a stray "(#)" or a lone dash next to a real id.
export const getCompanyLabel = (attempt?: IVendorQuoteImportAttempt): string => {
  const { companyId, companyName } = attempt ?? {};

  if (!companyId) {
    return companyName || DEFAULT_PLACEHOLDER;
  }

  if (!companyName) {
    return t('labelCompanyId', { id: companyId });
  }

  return t('labelCompanyIdWithName', { id: companyId, name: companyName });
};

// The numbered warning scale is inverted in the dark theme (theme.tsx maps
// warning.400 to #72430D and warning.600 to the #EA9A3E amber), so a step that
// reads as amber in light mode is near-black on a dark row — and this cell is
// icon-first, so the warning would vanish rather than dim. custom.warning.main
// (#FB8C00) is the one amber defined identically in both schemes, and is what
// the rest of the app uses for a warning icon (see CostExceedsTooltip).
// danger.500 is #C41C1C in both schemes, so it needs no such treatment.
const WARNING_ICON_COLOR = 'custom.warning.main';
const DANGER_ICON_COLOR = `${Color.DANGER}.500`;

export interface IManufacturerStatusWarning {
  color: string;
  label: string;
  tooltip: string;
}

// An Imported Manufacturer cell with no value explains why: the extraction
// never persisted, only discount rows were staged, or the payload carried no
// manufacturer for the first part item. RESOLVED needs no warning — it covers
// a received name that matched no manufacturer record, which is the Partfiniti
// column's placeholder to show, not this column's.
export const getManufacturerStatusWarning = (
  attempt?: IVendorQuoteImportAttempt
): IManufacturerStatusWarning | null => {
  // An upload still running has not persisted anything *yet*. Every message here
  // is past tense ("the import failed before any part data was saved"), so
  // showing one mid-flight reports a failure that has not happened.
  if (attempt?.outcome === VendorQuoteImportOutcome.IN_PROGRESS) {
    return null;
  }

  switch (attempt?.manufacturerStatus) {
    case VendorQuoteImportManufacturerStatus.NOT_PERSISTED:
      return {
        color: DANGER_ICON_COLOR,
        label: t('labelImportNotPersisted'),
        tooltip: t('tooltipManufacturerNotPersisted'),
      };
    case VendorQuoteImportManufacturerStatus.DISCOUNTS_ONLY:
      return {
        color: WARNING_ICON_COLOR,
        label: t('labelDiscountItemsOnly'),
        tooltip: t('tooltipManufacturerDiscountsOnly'),
      };
    case VendorQuoteImportManufacturerStatus.NO_MANUFACTURER:
      return {
        color: DANGER_ICON_COLOR,
        label: t('labelNoManufacturerReceived'),
        tooltip: t('tooltipManufacturerNoneReceived'),
      };
    default:
      return null;
  }
};

// The row-level import errors never reach the log stream, so this is the only
// account of why an import landed nothing. Identical messages are already
// grouped server-side; the count says how many rows each covers.
const getFailureReasonLabels = (reasons?: IVendorQuoteImportFailureReason[]): string =>
  (reasons ?? [])
    .map(({ message, count }) =>
      count > 1 ? t('labelFailureReasonWithCount', { count, message }) : message
    )
    .join('; ');

// Everything that went wrong with an upload, in one line: the attempt-level
// error from the log stream, then why its part rows failed. They answer the
// same question at different granularity, so they belong in the same cell
// rather than in two columns the reader has to correlate.
export const getImportErrorLabel = (attempt: IVendorQuoteImportAttempt): string =>
  // An em dash, not a space: the attempt error is server text that may not end in
  // punctuation, and a bare space runs it straight into the row reason
  // ("Import timed out178 × The part could not be imported").
  [attempt?.error, getFailureReasonLabels(attempt?.failureReasons)].filter(Boolean).join(' — ');

// A discounts-only upload is a normal, successful one that simply had no part
// item to take a manufacturer from — which is why its icon is amber and its
// tooltip explains it as expected. The toggle promises "uploads that need
// attention", so this is not one of them; the icon still says so in the column.
const hasManufacturerProblem = (attempt: IVendorQuoteImportAttempt): boolean =>
  attempt?.manufacturerStatus !== VendorQuoteImportManufacturerStatus.DISCOUNTS_ONLY &&
  !!getManufacturerStatusWarning(attempt);

// A row worth looking at: it failed, it reported an error, it raised an
// anomaly, part rows failed to import, or the manufacturer cell is carrying a
// warning. Drives the table's default "problems only" view.
export const hasImportProblem = (attempt: IVendorQuoteImportAttempt): boolean =>
  attempt?.outcome === VendorQuoteImportOutcome.FAILED ||
  !!attempt?.error ||
  !!attempt?.anomalies?.length ||
  (attempt?.failedPartCount ?? 0) > 0 ||
  hasManufacturerProblem(attempt);

export const formatDurationSeconds = (durationSeconds?: number | null): string => {
  if (durationSeconds == null) {
    return DEFAULT_PLACEHOLDER;
  }

  // Round the total before splitting: rounding the remainder instead lets it reach 60
  // ("1m 60s" for 119.7), and avgDurationSeconds is a mean, so fractions are the norm.
  const totalSeconds = Math.round(durationSeconds);

  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;

  if (minutes === 0) {
    return t('labelDurationSeconds', { seconds });
  }

  return t('labelDurationMinutesSeconds', { minutes, seconds });
};
